/**
 * Blog product picks
 *
 * Turns an authored marker inside an article body into live, buyable product
 * cards. Prices, stock and variants come from /products/<handle>.js at render
 * time, so nothing in the article HTML can go stale.
 *
 * Authoring:
 *   <div data-aasra-pick
 *        data-layout="hero|list"
 *        data-eyebrow="In short"
 *        data-title="What to buy"
 *        data-items='[{"handle":"side-opening-recovery-pants","reason":"...","badge":"Our pick"}]'></div>
 *
 * Add to cart goes through window.CartAPI so the cart drawer's automatic
 * BUY2AASRA / BUY3AASRA discount logic fires exactly as it does elsewhere.
 */
(function () {
  'use strict';

  var cache = {};

  function money(cents) {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return '₹' + Math.round(cents / 100);
    }
  }

  function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .catch(function () { return null; });
    return cache[handle];
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Which option indexes actually need a control (more than one distinct value)
  function activeOptions(product) {
    return (product.options || []).map(function (opt, i) {
      var name = typeof opt === 'string' ? opt : opt.name;
      var values = [];
      product.variants.forEach(function (v) {
        var val = v.options[i];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      return { index: i, name: name, values: values };
    }).filter(function (o) {
      return o.values.length > 1;
    }).sort(function (a, b) {
      // Size is the decision people actually pause on, so it leads -- and the
      // ordering stays the same card to card regardless of each product's
      // own option order.
      var as = /size/i.test(a.name) ? 0 : 1;
      var bs = /size/i.test(b.name) ? 0 : 1;
      return as - bs;
    });
  }

  function resolveVariant(product, selected) {
    return product.variants.find(function (v) {
      return Object.keys(selected).every(function (i) {
        return v.options[i] === selected[i];
      });
    }) || null;
  }

  // Product titles in Shopify are long SEO strings ("Men's Velcro Pajamas |
  // Side-Opening Pyjamas for Surgery Recovery | No Step-In Needed"). Cards show
  // a clean name instead. The two pyjama entries are explicit because the
  // catalogue title uses wording we do not use in customer-facing copy.
  var DISPLAY_NAMES = {
    'side-opening-recovery-pants': "Men's Side-Opening Pyjamas",
    'side-opening-pajama-women': "Women's Side-Opening Pyjamas"
  };

  function displayName(product) {
    if (DISPLAY_NAMES[product.handle]) return DISPLAY_NAMES[product.handle];
    // Otherwise take the author's own short name: the part before the first
    // pipe, which is how these titles are structured.
    return product.title.split('|')[0].replace(/[\s\u2013\u2014-]+$/, '').trim() || product.title;
  }

  function sizeRangeLabel(product) {
    var opt = (product.options || []).map(function (o, i) {
      return { name: typeof o === 'string' ? o : o.name, i: i };
    }).find(function (o) { return /size/i.test(o.name); });
    if (!opt) return '';
    var vals = [];
    product.variants.forEach(function (v) {
      var val = v.options[opt.i];
      if (val && vals.indexOf(val) === -1) vals.push(val);
    });
    if (vals.length < 2) return '';
    return 'Sizes ' + vals[0] + ' to ' + vals[vals.length - 1];
  }

  function buildCard(product, item, layout) {
    var hero = layout === 'hero';
    var url = '/products/' + product.handle;

    // The whole card is a link. Blog readers are researching, not checking out:
    // the job here is to hand them the right product page to explore, not to
    // take an order inside an article.
    var card = el('a', 'aasra-pick' + (hero ? ' aasra-pick--hero' : ''));
    card.href = url;

    var media = el('span', 'aasra-pick__media');
    var src = product.featured_image || (product.images && product.images[0]);
    if (src) {
      var img = document.createElement('img');
      img.src = src.replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2');
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 400; img.height = 400;
      media.appendChild(img);
    }
    card.appendChild(media);

    var body = el('span', 'aasra-pick__body');

    if (item.badge) body.appendChild(el('span', 'aasra-pick__badge', item.badge));
    body.appendChild(el('span', 'aasra-pick__name', displayName(product)));
    if (item.reason) body.appendChild(el('span', 'aasra-pick__reason', item.reason));

    // Price qualifies the click, so it stays even though buying happens on the
    // product page.
    var cheapest = product.variants.reduce(function (lo, v) {
      return (!lo || v.price < lo.price) ? v : lo;
    }, null);
    if (cheapest) {
      var priceWrap = el('span', 'aasra-pick__price');
      priceWrap.appendChild(el('span', 'aasra-pick__price-now', money(cheapest.price)));
      if (cheapest.compare_at_price && cheapest.compare_at_price > cheapest.price) {
        priceWrap.appendChild(el('span', 'aasra-pick__price-was', money(cheapest.compare_at_price)));
      }
      body.appendChild(priceWrap);
    }

    var meta = [];
    var sizes = sizeRangeLabel(product);
    if (sizes) meta.push(sizes);
    if (!product.available) meta.push('Currently out of stock');
    if (meta.length) body.appendChild(el('span', 'aasra-pick__meta', meta.join('  \u00B7  ')));

    body.appendChild(el('span', 'aasra-pick__cta', 'View product'));

    card.appendChild(body);
    return card;
  }

  function hydrate(node) {
    if (node.hasAttribute('data-picks-ready')) return;
    node.setAttribute('data-picks-ready', '');

    var items;
    try {
      items = JSON.parse(node.getAttribute('data-items') || '[]');
    } catch (e) {
      node.hidden = true;
      return;
    }
    if (!items.length) { node.hidden = true; return; }

    var layout = node.getAttribute('data-layout') || 'list';

    Promise.all(items.map(function (it) { return fetchProduct(it.handle); }))
      .then(function (products) {
        var frag = document.createDocumentFragment();

        var eyebrow = node.getAttribute('data-eyebrow');
        var title = node.getAttribute('data-title');
        if (eyebrow) frag.appendChild(el('span', 'aasra-picks__eyebrow', eyebrow));
        if (title) frag.appendChild(el('h3', 'aasra-picks__title', title));

        var list = el('div', 'aasra-picks__list');
        var rendered = 0;
        products.forEach(function (p, i) {
          if (!p) return;
          list.appendChild(buildCard(p, items[i], i === 0 ? layout : 'list'));
          rendered++;
        });

        if (!rendered) { node.hidden = true; return; }
        frag.appendChild(list);
        node.innerHTML = '';
        node.className = 'aasra-picks ' + (node.className || '').replace('aasra-picks', '').trim();
        node.appendChild(frag);
      });
  }


  /* ------------------------------------------------------------------
   * Legacy upgrade
   *
   * Most articles were authored with hand-coded product grids: inline-styled
   * divs holding <a href="/products/..."><img>...</a> cards with no price, no
   * stock and no add to cart. Rather than rewrite 80+ article bodies, we find
   * those blocks, read the product handles out of them, and replace them with
   * live cards. We also lift a short "what to buy" block near the top, because
   * the original blocks sit 7+ screens down (often below the FAQs).
   * ------------------------------------------------------------------ */

  // Article handles the upgrade runs on. Set to '*' to enable everywhere.
  var ENABLED = [
    'best-clothing-for-senior-citizens-india-2026',
    'adaptive-clothing-brands-india',
    'what-to-wear-after-knee-replacement-surgery-india'
  ];

  // Per-article copy. Keys are article handles.
  var CURATION = {
    'best-clothing-for-senior-citizens-india-2026': {
      earlyTitle: 'Not sure where to start?',
      earlyEyebrow: 'In short',
      early: [
        { handle: 'easy-front-open-recovery-nighty', badge: 'Most chosen for mothers',
          reason: 'Opens all the way down the front with soft velcro, so it never goes over the head. The easiest first change to make.' },
        { handle: 'side-opening-recovery-pants', badge: 'Most chosen for fathers',
          reason: 'Opens along both sides with velcro and has an elastic waist, so he can dress sitting on the bed without standing or stepping in.' }
      ],
      mainTitle: 'Easy-wear clothing for seniors',
      reasons: {
        'easy-front-open-recovery-nighty': 'Front-open nighty in soft cotton. No overhead lifting, easy for night-time changes.',
        'easy-front-kurti': 'Looks like a regular kurti, opens fully at the front with concealed velcro.',
        'side-opening-pajama-women': 'Opens along both sides, so she can be dressed seated or lying down.',
        'side-opening-recovery-pants': 'Elastic waist with velcro side openings. No drawstrings to tie.'
      }
    },
    'what-to-wear-after-knee-replacement-surgery-india': {
      earlyTitle: 'What most knee replacement patients need',
      earlyEyebrow: 'In short',
      early: [
        { handle: 'side-opening-recovery-pants', badge: 'Our pick',
          reason: 'Opens fully along the outer seam, so it fits over a knee brace and your physio can reach the knee in seconds without you undressing.' },
        { handle: 'adaptive-tearaway-recovery-shorts', badge: 'For physio sessions',
          reason: 'Less fabric over a swollen knee, and easier access for ice packs and wound checks.' }
      ],
      mainTitle: 'Recommended for knee replacement recovery'
    },
    'adaptive-clothing-brands-india': {
      earlyTitle: 'Aasra’s most ordered pieces',
      earlyEyebrow: 'In short',
      early: [
        { handle: 'easy-front-open-recovery-nighty', badge: 'Best seller',
          reason: 'Front-open cotton nighty, sizes S to 2XL. The most common first order for an elderly mother.' },
        { handle: 'side-opening-recovery-pants', badge: 'Best seller',
          reason: 'Side-opening velcro pyjamas, sizes M to 2XL. Dress seated or lying down, no drawstring.' }
      ],
      mainTitle: 'Shop Aasra adaptive clothing'
    }
  };

  function currentArticleHandle() {
    var m = location.pathname.match(/\/blogs\/[^/]+\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function isEnabled(handle) {
    return ENABLED === '*' || (Array.isArray(ENABLED) && ENABLED.indexOf(handle) !== -1);
  }

  // Legacy blocks come in two shapes:
  //   a) a grid div whose direct children are <a> product cards
  //   b) a grid div whose children are per-card <div>s, each holding several
  //      links to the SAME product (image, title, "Shop Now")
  // Both reduce to: find each card's root element, then group the roots that
  // share a parent. Two or more roots under one parent is a product grid.
  function productHandlesIn(node) {
    var set = {};
    // The node itself counts: when a card root IS the <a> (a grid of bare
    // anchor cards), querySelectorAll alone finds nothing and the block is
    // skipped.
    var links = Array.prototype.slice.call(node.querySelectorAll('a[href*="/products/"]'));
    if (node.tagName === 'A' && (node.getAttribute('href') || '').indexOf('/products/') !== -1) {
      links.push(node);
    }
    links.forEach(function (a) {
      var h = handleFromHref(a.getAttribute('href'));
      if (h) set[h] = true;
    });
    return Object.keys(set);
  }

  function cardRootFor(link, stopAt) {
    var node = link;
    while (node.parentElement && node.parentElement !== stopAt) {
      var parent = node.parentElement;
      // Climbing is only safe while the ancestor still describes ONE product.
      if (productHandlesIn(parent).length > 1) break;
      node = parent;
    }
    return node;
  }

  function findLegacyBlocks(root) {
    var imgLinks = Array.prototype.slice.call(root.querySelectorAll('a[href*="/products/"]'))
      .filter(function (a) {
        // Skip cards we rendered ourselves, or init() would recycle its own
        // output as "legacy" on a second run.
        return a.querySelector('img') && !a.closest('.aasra-picks');
      });

    var groups = new Map();
    imgLinks.forEach(function (a) {
      var card = cardRootFor(a, root);
      var parent = card.parentElement;
      if (!parent) return;
      if (!groups.has(parent)) groups.set(parent, []);
      if (groups.get(parent).indexOf(card) === -1) groups.get(parent).push(card);
    });

    var blocks = [];
    groups.forEach(function (cards, parent) {
      if (cards.length < 2) return;
      blocks.push({ parent: parent, cards: cards });
    });
    return blocks;
  }

  function handleFromHref(href) {
    var m = (href || '').split('?')[0].match(/\/products\/([^/?#"']+)/);
    return m ? m[1].replace(/\\+$/, '') : null;
  }

  function makeMarker(items, opts) {
    var node = document.createElement('div');
    node.setAttribute('data-aasra-pick', '');
    node.setAttribute('data-items', JSON.stringify(items));
    if (opts.layout) node.setAttribute('data-layout', opts.layout);
    if (opts.title) node.setAttribute('data-title', opts.title);
    if (opts.eyebrow) node.setAttribute('data-eyebrow', opts.eyebrow);
    node.className = 'aasra-picks';
    return node;
  }

  // Walk back from the block (and out of its wrapper) looking for the heading
  // the author put above it.
  function findPrecedingHeading(node) {
    var hops = 0;
    var cur = node;
    while (cur && hops < 3) {
      var sib = cur.previousElementSibling;
      while (sib) {
        if (/^H[1-6]$/.test(sib.tagName)) return sib;
        if (sib.textContent.trim()) break; // real content in between
        sib = sib.previousElementSibling;
      }
      // the wrapper's own first child may be the heading
      if (cur.parentElement) {
        var first = cur.parentElement.firstElementChild;
        if (first && /^H[1-6]$/.test(first.tagName)) return first;
      }
      cur = cur.parentElement;
      hops++;
    }
    return null;
  }

  // Legacy callouts were authored with a green that predates the current
  // palette. Swap those inline colours for brand tokens so the article reads
  // as one design rather than two.
  var LEGACY_COLOURS = [
    [/#2d6a4f/gi, 'var(--color-primary-6, #2F3C7E)'],
    [/#1b4332/gi, 'var(--color-primary-7, #263166)'],
    [/#f0f7f4/gi, 'var(--color-primary-1, #F4F5FB)'],
    [/#e2e8e4/gi, 'var(--color-border-light, #E5E6EB)']
  ];

  function normaliseLegacyColours(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[style]'), function (node) {
      var style = node.getAttribute('style');
      if (!style) return;
      var next = style;
      LEGACY_COLOURS.forEach(function (pair) { next = next.replace(pair[0], pair[1]); });
      if (next !== style) node.setAttribute('style', next);
    });
  }

  var upgraded = false;
  function upgradeLegacy() {
    if (upgraded) return;
    upgraded = true;
    var articleHandle = currentArticleHandle();
    if (!articleHandle || !isEnabled(articleHandle)) return;

    var root = document.querySelector('.article-content');
    if (!root) return;

    var cfg = CURATION[articleHandle] || {};

    normaliseLegacyColours(root);

    // 1. Replace legacy grids with live cards
    var blocks = findLegacyBlocks(root);
    var seen = {};
    var mainHandles = [];

    blocks.forEach(function (b, i) {
      var items = [];
      // Dedupe within a block only. A product may legitimately appear in more
      // than one block (e.g. a general list and a section-specific one), and
      // deduping across blocks would leave the later block unconverted.
      var inBlock = {};
      b.cards.forEach(function (card) {
        var handles = productHandlesIn(card);
        var h = handles[0];
        if (!h || inBlock[h]) return;
        inBlock[h] = true;
        if (!seen[h]) { seen[h] = true; mainHandles.push(h); }
        items.push({ handle: h, reason: (cfg.reasons && cfg.reasons[h]) || '' });
      });
      if (!items.length) return;

      // Keep an author-written heading above the block -- it belongs to the
      // document outline -- and skip our own title so it is not shown twice.
      // Legacy headings carry inline colours that predate the palette.
      var heading = findPrecedingHeading(b.cards[0]);
      if (heading) heading.removeAttribute('style');

      var marker = makeMarker(items, {
        layout: 'list',
        title: (!heading && i === 0) ? (cfg.mainTitle || '') : ''
      });

      b.parent.insertBefore(marker, b.cards[0]);
      b.cards.forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });

      // A wrapper left holding nothing but our marker adds stray grid styles.
      if (b.parent !== root && b.parent.children.length === 1) {
        b.parent.removeAttribute('style');
      }
    });

    // 2. Inject an early decision block after the opening paragraphs
    var earlyItems = cfg.early;
    if (!earlyItems || !earlyItems.length) {
      if (!mainHandles.length) return;
      earlyItems = mainHandles.slice(0, 2).map(function (h) { return { handle: h, reason: '' }; });
    }
    var paras = root.querySelectorAll(':scope > p');
    var anchor = paras[1] || paras[0];
    if (!anchor) return;

    var early = makeMarker(earlyItems, {
      layout: 'hero',
      title: cfg.earlyTitle || 'What to buy',
      eyebrow: cfg.earlyEyebrow || 'In short'
    });
    early.classList.add('aasra-picks--early');
    anchor.parentNode.insertBefore(early, anchor.nextSibling);
  }

  function init() {
    upgradeLegacy();
    document.querySelectorAll('[data-aasra-pick]').forEach(hydrate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
